// =============================================================================
// ApparelSidesTab — admin tab for apparel multi-side templates
// -----------------------------------------------------------------------------
// Lists every active PrintArea on the UPT. Supports:
//   • Add Side (preset picker + custom name) — auto-creates "Front" lives
//     inside CatalogService.createTemplate so this tab is never empty by
//     accident; the empty-state surfaces the same Add Side affordance
//     defensively in case every side is removed.
//   • Soft delete (last-active guard server-side; UI also disables the
//     delete button when only one row remains)
//   • dnd-kit drag-to-reorder (optimistic UI; server confirms via single
//     batch PUT — see SidesService.reorderPrintAreas)
//   • Per-side mockup upload (POST /print-areas/:id/mockup-upload)
//   • Per-side settings PATCH (customizationMandatory, maxCanvasIn)
//
// Konva print-area editor (drag/resize the rectangle on the mockup) and
// the per-variant override matrix land in Phases 3b and 3c. This tab
// renders placeholders for both so the layout is final.
// =============================================================================

import { lazy, Suspense, useCallback, useMemo, useState } from 'react';
import {
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { Card, Button, Spinner, Badge, useConfirm } from '@tkpod/ui';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { toast } from '@/lib/toast';
import {
  createPrintArea,
  deletePrintArea,
  listPrintAreas,
  reorderPrintAreas,
  updatePrintArea,
  uploadPrintAreaMockup,
  type CreatePrintAreaPayload,
  type PrintAreaSummary,
  type UpdatePrintAreaPayload,
} from '@/lib/api/apparel-sides';
import { PrintAreaCard } from './PrintAreaCard';
import { AddSideDropdown } from './AddSideDropdown';
import { VariantOverridesPanel } from './VariantOverridesPanel';

// Lazy-load the editor — it ships its own pointer/geometry logic and is
// only mounted when the merchant actually opens it. Keeps the catalog
// detail page's first paint snappy.
const PrintAreaEditorModal = lazy(() =>
  import('./PrintAreaEditorModal').then((m) => ({
    default: m.PrintAreaEditorModal,
  })),
);

// Same rationale as PrintAreaEditorModal: only loaded the first time
// a merchant clicks Edit side or adds a new side. The modal carries
// its own controlled inputs + validation copy, so eagerly bundling it
// would punish the much more common "viewing the sides list" flow.
const EditSideMetaModal = lazy(() =>
  import('./EditSideMetaModal').then((m) => ({
    default: m.EditSideMetaModal,
  })),
);

const QUERY_KEY = (templateId: string) => ['admin-print-areas', templateId];

export function ApparelSidesTab({ templateId }: { templateId: string }) {
  const qc = useQueryClient();
  const { confirm, dialog } = useConfirm();

  // ---------------------------------------------------------------------------
  // Query — print area list (apparel sides)
  // ---------------------------------------------------------------------------
  const { data: printAreas = [], isLoading, isError, error, refetch } = useQuery<
    PrintAreaSummary[]
  >({
    queryKey: QUERY_KEY(templateId),
    queryFn: () => listPrintAreas(templateId),
  });

  // ---------------------------------------------------------------------------
  // dnd-kit sensors + reorder mutation (optimistic)
  // ---------------------------------------------------------------------------
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const reorderMutation = useMutation({
    mutationFn: (orderedIds: string[]) =>
      reorderPrintAreas(templateId, orderedIds),
    onMutate: async (orderedIds) => {
      await qc.cancelQueries({ queryKey: QUERY_KEY(templateId) });
      const prev = qc.getQueryData<PrintAreaSummary[]>(QUERY_KEY(templateId));
      if (prev) {
        const indexById = new Map(orderedIds.map((id, i) => [id, i]));
        const next = [...prev]
          .map((p) => ({ ...p, sortOrder: indexById.get(p.id) ?? p.sortOrder }))
          .sort((a, b) => a.sortOrder - b.sortOrder);
        qc.setQueryData<PrintAreaSummary[]>(QUERY_KEY(templateId), next);
      }
      return { prev };
    },
    onError: (err: Error, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(QUERY_KEY(templateId), ctx.prev);
      toast(err.message || 'Failed to reorder print areas.', 'error');
    },
    onSuccess: (data) => {
      qc.setQueryData<PrintAreaSummary[]>(QUERY_KEY(templateId), data);
    },
  });

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = printAreas.findIndex((p) => p.id === active.id);
      const newIndex = printAreas.findIndex((p) => p.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;
      const reordered = arrayMove(printAreas, oldIndex, newIndex);
      reorderMutation.mutate(reordered.map((p) => p.id));
    },
    [printAreas, reorderMutation],
  );

  // ---------------------------------------------------------------------------
  // Mutations — per-area mockup upload + settings PATCH
  // ---------------------------------------------------------------------------

  const [uploadingId, setUploadingId] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);

  const uploadMutation = useMutation({
    mutationFn: ({ printAreaId, file }: { printAreaId: string; file: File }) =>
      uploadPrintAreaMockup(templateId, printAreaId, file),
    onMutate: ({ printAreaId }) => setUploadingId(printAreaId),
    onSuccess: (updated) => {
      qc.setQueryData<PrintAreaSummary[]>(QUERY_KEY(templateId), (prev) =>
        (prev ?? []).map((p) => (p.id === updated.id ? { ...p, ...updated } : p)),
      );
      toast('Mockup uploaded.');
    },
    onError: (err: Error) => {
      toast(err.message || 'Mockup upload failed.', 'error');
    },
    onSettled: () => setUploadingId(null),
  });

  const patchMutation = useMutation({
    mutationFn: ({
      printAreaId,
      payload,
    }: {
      printAreaId: string;
      payload: UpdatePrintAreaPayload;
    }) => updatePrintArea(templateId, printAreaId, payload),
    onMutate: ({ printAreaId }) => setSavingId(printAreaId),
    onSuccess: (updated) => {
      qc.setQueryData<PrintAreaSummary[]>(QUERY_KEY(templateId), (prev) =>
        (prev ?? []).map((p) => (p.id === updated.id ? { ...p, ...updated } : p)),
      );
      toast('Side updated.');
    },
    onError: (err: Error) => {
      toast(err.message || 'Failed to update side.', 'error');
    },
    onSettled: () => setSavingId(null),
  });

  // Clear-mockup is a PATCH with empty string — the DTO conditionally
  // validates `mockupImageUrl` only when non-empty, and the service maps
  // `''` -> `null` before persisting. Old S3 key cleanup happens on the
  // next mockup upload (uploadMockup deletes the previous key).
  const clearMockup = useCallback(
    (printAreaId: string) => {
      patchMutation.mutate({
        printAreaId,
        payload: { mockupImageUrl: '' },
      });
    },
    [patchMutation],
  );

  // ---------------------------------------------------------------------------
  // Mutations — create / delete sides
  // ---------------------------------------------------------------------------

  // Drives both `Edit side` (mode='edit') and the auto-open after Add
  // Side (mode='create'). Stored as a tagged tuple instead of two
  // booleans so the lazy-mounted modal sees a single re-render cycle
  // and never displays the wrong CTA between transitions.
  const [editingMeta, setEditingMeta] = useState<
    | { id: string; mode: 'create' | 'edit' }
    | null
  >(null);

  const createSideMutation = useMutation({
    mutationFn: (payload: CreatePrintAreaPayload) =>
      createPrintArea(templateId, payload),
    onSuccess: (created) => {
      // Server returns the new row; insert at the end of the cached list
      // (sortOrder = max + 1). Refetch in the background to pick up any
      // count drift from concurrent mutations.
      qc.setQueryData<PrintAreaSummary[]>(QUERY_KEY(templateId), (prev) => {
        const list = prev ?? [];
        if (list.some((p) => p.id === created.id)) return list;
        return [...list, created].sort((a, b) => a.sortOrder - b.sortOrder);
      });
      void qc.invalidateQueries({ queryKey: QUERY_KEY(templateId) });
      toast(`"${created.displayName}" added.`);
      // Auto-open the meta modal so the merchant configures dimensions
      // before doing anything else with the new side. Reflects the
      // user-locked decision in the side editor refactor plan
      // (apparel_side_editor_refactor_a7c91d4e §2 row 4).
      setEditingMeta({ id: created.id, mode: 'create' });
    },
    onError: (err: Error) => {
      toast(err.message || 'Failed to add side.', 'error');
    },
  });

  const deleteSideMutation = useMutation({
    mutationFn: (printAreaId: string) =>
      deletePrintArea(templateId, printAreaId),
    onMutate: async (printAreaId) => {
      await qc.cancelQueries({ queryKey: QUERY_KEY(templateId) });
      const prev = qc.getQueryData<PrintAreaSummary[]>(QUERY_KEY(templateId));
      if (prev) {
        qc.setQueryData<PrintAreaSummary[]>(
          QUERY_KEY(templateId),
          prev.filter((p) => p.id !== printAreaId),
        );
      }
      return { prev };
    },
    onError: (err: Error, _vars, ctx) => {
      // Server enforces "≥1 active side" — surface the 409 message
      // verbatim so the merchant knows why the row stayed.
      if (ctx?.prev) qc.setQueryData(QUERY_KEY(templateId), ctx.prev);
      toast(err.message || 'Failed to remove side.', 'error');
    },
    onSuccess: (data) => {
      qc.setQueryData<PrintAreaSummary[]>(QUERY_KEY(templateId), data);
      toast('Side removed.');
    },
  });

  const handleDeleteSide = useCallback(
    async (printAreaId: string, displayName: string) => {
      if (printAreas.length <= 1) {
        toast(
          'You cannot remove the last side. Add another side first.',
          'error',
        );
        return;
      }
      const confirmed = await confirm({
        title: `Remove "${displayName}"?`,
        description:
          'This hides the side from the designer and from storefront product previews. Products using this template need to be republished so Shopify media drops the side. Existing orders keep their print files.',
        confirmLabel: 'Remove side',
        tone: 'danger',
      });
      if (!confirmed) return;
      deleteSideMutation.mutate(printAreaId);
    },
    [confirm, deleteSideMutation, printAreas.length],
  );

  const existingSlugs = useMemo(
    () => new Set(printAreas.map((p) => p.slug)),
    [printAreas],
  );

  // ---------------------------------------------------------------------------
  // PrintAreaEditorModal — open/close state. The modal patches three fields
  // together (`printAreaRelative + widthIn + heightIn`) so the relative rect
  // and the inches that anchor its aspect ratio stay coherent.
  // ---------------------------------------------------------------------------

  const [editingId, setEditingId] = useState<string | null>(null);
  const editingArea = useMemo(
    () => (editingId ? printAreas.find((p) => p.id === editingId) : undefined),
    [editingId, printAreas],
  );

  const editorMutation = useMutation({
    mutationFn: ({
      printAreaId,
      payload,
    }: {
      printAreaId: string;
      payload: UpdatePrintAreaPayload;
    }) => updatePrintArea(templateId, printAreaId, payload),
    onSuccess: (updated) => {
      qc.setQueryData<PrintAreaSummary[]>(QUERY_KEY(templateId), (prev) =>
        (prev ?? []).map((p) => (p.id === updated.id ? { ...p, ...updated } : p)),
      );
      toast('Print area saved.');
      setEditingId(null);
    },
    onError: (err: Error) => {
      toast(err.message || 'Failed to save print area.', 'error');
    },
  });

  // ---------------------------------------------------------------------------
  // EditSideMetaModal — displayName + width + height save path. Reuses
  // `updatePrintArea` since the API DTO accepts all three on a single
  // PATCH. Keeping the mutation separate from `editorMutation` (which
  // also patches widthIn/heightIn but together with the relative rect)
  // avoids over-broad cache writes when the meta modal lands.
  // ---------------------------------------------------------------------------

  const editingMetaArea = useMemo(
    () =>
      editingMeta
        ? printAreas.find((p) => p.id === editingMeta.id)
        : undefined,
    [editingMeta, printAreas],
  );

  const metaMutation = useMutation({
    mutationFn: ({
      printAreaId,
      payload,
    }: {
      printAreaId: string;
      payload: UpdatePrintAreaPayload;
    }) => updatePrintArea(templateId, printAreaId, payload),
    onSuccess: (updated) => {
      qc.setQueryData<PrintAreaSummary[]>(QUERY_KEY(templateId), (prev) =>
        (prev ?? []).map((p) => (p.id === updated.id ? { ...p, ...updated } : p)),
      );
      toast('Side updated.');
      setEditingMeta(null);
    },
    onError: (err: Error) => {
      toast(err.message || 'Failed to update side.', 'error');
    },
  });

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const orderedIds = useMemo(() => printAreas.map((p) => p.id), [printAreas]);

  if (isLoading) {
    return (
      <Card>
        <div className="flex items-center justify-center px-6 py-16">
          <Spinner />
        </div>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card>
        <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
          <h3 className="text-base font-semibold text-text-primary">
            Could not load apparel sides
          </h3>
          <p className="max-w-md text-sm text-text-secondary">
            {error instanceof Error ? error.message : 'Unknown error'}
          </p>
          <Button size="sm" variant="outline" onClick={() => void refetch()}>
            Retry
          </Button>
        </div>
      </Card>
    );
  }

  if (printAreas.length === 0) {
    // Backend auto-creates "Front" on apparel template create, so this
    // branch only fires after every side has been removed (rare). Keep
    // the Add Side affordance visible so the merchant can recover.
    return (
      <Card>
        <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
          <h3 className="text-base font-semibold text-text-primary">
            No print areas yet
          </h3>
          <p className="max-w-md text-sm text-text-secondary">
            Add a print area (Front, Back, Sleeve…) to start configuring
            mockups, customization rules and per-variant overrides for this
            apparel template.
          </p>
          <div className="pt-2">
            <AddSideDropdown
              existingSlugs={existingSlugs}
              isCreating={createSideMutation.isPending}
              onPick={(payload) => createSideMutation.mutate(payload)}
            />
          </div>
        </div>
      </Card>
    );
  }

  const canDelete = printAreas.length > 1;
  const deletingId = deleteSideMutation.isPending
    ? (deleteSideMutation.variables as string | undefined)
    : undefined;

  return (
    <div className="space-y-4">
      {dialog}
      {/* Header card */}
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-text-primary">
              Print Areas & Variants
            </h2>
            <p className="mt-1 text-sm text-text-secondary">
              {printAreas.length} active side
              {printAreas.length === 1 ? '' : 's'}. Drag the handle to reorder
              — the designer renders sides in this order.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="default" size="sm">
              APPAREL multi-side
            </Badge>
            <AddSideDropdown
              existingSlugs={existingSlugs}
              isCreating={createSideMutation.isPending}
              onPick={(payload) => createSideMutation.mutate(payload)}
            />
          </div>
        </div>
      </Card>

      {/* Sortable list */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={orderedIds} strategy={verticalListSortingStrategy}>
          <div className="space-y-3">
            {printAreas.map((pa, idx) => (
              <PrintAreaCard
                key={pa.id}
                printArea={pa}
                index={idx}
                isUploadingMockup={uploadingId === pa.id}
                isSavingSettings={savingId === pa.id}
                isReordering={reorderMutation.isPending}
                isDeleting={deletingId === pa.id}
                canDelete={canDelete}
                onUploadMockup={(file) =>
                  uploadMutation.mutate({ printAreaId: pa.id, file })
                }
                onClearMockup={() => clearMockup(pa.id)}
                onPatch={(payload) =>
                  patchMutation.mutate({ printAreaId: pa.id, payload })
                }
                onDelete={() => handleDeleteSide(pa.id, pa.displayName)}
                onEditPrintArea={() => setEditingId(pa.id)}
                onEditSideMeta={() =>
                  setEditingMeta({ id: pa.id, mode: 'edit' })
                }
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {/* Variant-overrides matrix renders for BOTH mockup modes — the
          panel itself is mode-aware:
            • MASK sides   → "Background color" column (drives the
              variant fill rendered behind the colorless mask PNG).
            • PER_VARIANT  → "Mockup image" column (the per-variant
              fully-rendered photo that replaces the side default).
          Auto-detect colors banner only renders in MASK mode. The
          per-variant print-area editor button only renders in
          PER_VARIANT mode. See VariantOverridesPanel for the column
          contract. */}
      <VariantOverridesPanel
        templateId={templateId}
        printAreas={printAreas}
      />

      {/* Print area editor — lazy-mounted only while open */}
      {editingArea && (
        <Suspense fallback={null}>
          <PrintAreaEditorModal
            open={true}
            printArea={editingArea}
            saving={editorMutation.isPending}
            onClose={() => {
              if (editorMutation.isPending) return;
              setEditingId(null);
            }}
            onSave={(payload) =>
              editorMutation.mutate({
                printAreaId: editingArea.id,
                payload,
              })
            }
          />
        </Suspense>
      )}

      {/* Side meta editor — same lazy-mount pattern as the print-area
          editor. Uses `key={editingMetaArea.id}` so switching between
          two sides resets the controlled inputs cleanly without
          plumbing through useEffect dependencies. */}
      {editingMeta && editingMetaArea && (
        <Suspense fallback={null}>
          <EditSideMetaModal
            key={editingMetaArea.id}
            open={true}
            printArea={editingMetaArea}
            mode={editingMeta.mode}
            saving={metaMutation.isPending}
            onClose={() => {
              if (metaMutation.isPending) return;
              setEditingMeta(null);
            }}
            onSave={(payload) =>
              metaMutation.mutate({
                printAreaId: editingMetaArea.id,
                payload: {
                  displayName: payload.displayName,
                  widthIn: payload.widthIn,
                  heightIn: payload.heightIn,
                  sizingMode: payload.sizingMode,
                },
              })
            }
          />
        </Suspense>
      )}
    </div>
  );
}
